import React, { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { getSupabaseClient } from '../../services/supabase';
import { usePostHog } from 'posthog-js/react';
import { trackAuthEvent } from '../../utils/trackAuthEvent';
import { markSignupRecoveryDone } from '../../utils/signupRecovery';
import { deriveCohort } from '../../utils/cohort';
import Portal from '../Portal';

export interface OtpModalProps {
  email: string;
  onClose: () => void;
  onEditEmail: () => void;
  onVerified: () => void;
}

export default function OtpModal({ email, onClose, onEditEmail, onVerified }: OtpModalProps) {
  const posthog = usePostHog();
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(30);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const modalOpenedAtRef = useRef(Date.now());
  const otpSentAtRef = useRef(Date.now());
  const isFacebookBrowser = useRef(/FBAN|FBAV/.test(navigator.userAgent)).current;

  // Track OTP modal view on mount
  useEffect(() => {
    posthog.capture('otp_modal_view', {
      email_domain: email.split('@')[1]?.toLowerCase(),
      is_facebook_browser: isFacebookBrowser,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Countdown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleInputChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError(null);

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Move to previous input on backspace
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    // Only accept 6 digits
    if (!/^\d{6}$/.test(pastedData)) {
      return;
    }

    const digits = pastedData.split('');
    const newCode = [...code];
    digits.forEach((digit, index) => {
      if (index < 6) {
        newCode[index] = digit;
      }
    });
    setCode(newCode);
    setError(null);

    // Focus last input
    inputRefs.current[5]?.focus();
  };

  const handleVerify = async () => {
    const otpCode = code.join('');
    
    if (otpCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    // Raw posthog.capture intentional (bypasses useTracking's 10s dedup) so
    // every grant attempt is recorded, including rapid back-to-back retries.
    const grantProps = (extra: Record<string, any> = {}) => ({
      provider: 'email_otp',
      cohort: deriveCohort(navigator.userAgent),
      release: process.env.REACT_APP_RELEASE ?? 'unknown',
      ms_since_page_load: Math.round(performance.now()),
      ms_since_otp_sent: Date.now() - otpSentAtRef.current,
      online: navigator.onLine,
      visibility: document.visibilityState,
      is_facebook_browser: isFacebookBrowser,
      email_domain: email.split('@')[1]?.toLowerCase(),
      ...extra,
    });
    const grantOutcome = (outcome: string, extra: Record<string, any> = {}) =>
      posthog.capture('auth_grant_outcome', { outcome, ...grantProps(extra) });

    // Gate the outer-catch grant_outcome on a successful verifyOtp so verify-
    // level failures don't pollute the grant denominator.
    let verifyOk = false;

    posthog.capture('otp_code_submitted', {
      time_since_otp_sent_ms: Date.now() - otpSentAtRef.current,
      is_facebook_browser: isFacebookBrowser,
    });

    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: otpCode,
        type: 'email',
      });

      if (error) {
        posthog.capture('otp_verification_failed', {
          error_message: error.message,
          is_facebook_browser: isFacebookBrowser,
        });
        setError(error.message || 'Invalid code. Please try again.');
        setLoading(false);
        return;
      }

      verifyOk = true;

      posthog.capture('auth_grant_started', grantProps());

      Sentry.addBreadcrumb({
        category: 'auth-handoff',
        message: 'verifyOtp_succeeded',
        level: 'info',
        data: { path: 'otp_modal' },
      });

      // Get session and call signupUser to grant credits
      const { data: { session } } = await supabase.auth.getSession();

      Sentry.addBreadcrumb({
        category: 'auth-handoff',
        message: 'getSession_returned',
        level: 'info',
        data: { path: 'otp_modal', has_session: !!session },
      });

      if (session) {
        let isNewUser = false;
        try {
          Sentry.addBreadcrumb({
            category: 'auth-handoff',
            message: 'signupUser_started',
            level: 'info',
            data: { path: 'otp_modal' },
          });
          const { signupUser } = await import('../../services/credits');
          const signupResult = await signupUser(session.user.email!, session.user.id, session.user.user_metadata?.full_name);
          Sentry.addBreadcrumb({
            category: 'auth-handoff',
            message: 'signupUser_returned',
            level: 'info',
            data: {
              path: 'otp_modal',
              signup_bonus_granted: signupResult.signup_bonus_granted,
              credits: signupResult.credits,
            },
          });
          grantOutcome(
            signupResult.signup_bonus_granted ? 'granted' : 'returned_not_granted',
            {
              supabase_user_id: session.user.id,
              signup_bonus_granted: signupResult.signup_bonus_granted,
              grant_status: signupResult.grant_status ?? null,
              http_status: 200,
            }
          );
          isNewUser = signupResult.signup_bonus_granted === true;
          // [Mitigation B] Mark recovery done so AuthContext's recovery
          // effect skips re-firing signupUser for this user.
          markSignupRecoveryDone(session.user.id);
        } catch (signupError) {
          // Distinguish chunk-load failures (dynamic import couldn't load
          // the credits service) from API-call failures (signupUser fetch
          // returned non-2xx or network-errored). Wrap in a distinct error
          // message prefix so it bypasses sentry.ts's ignoreErrors filter
          // (which strips bare "Failed to fetch" / "NetworkError" noise).
          const errMsg = signupError instanceof Error ? signupError.message : String(signupError);
          const errName = signupError instanceof Error ? signupError.name : 'UnknownError';
          const isChunkLoad =
            errName === 'ChunkLoadError' ||
            errMsg.includes('dynamically imported module') ||
            errMsg.includes('Loading chunk');
          const stage = isChunkLoad ? 'chunk_load' : 'post_verify_signup';

          Sentry.captureException(
            new Error(`[auth-handoff] ${stage} failed: ${errMsg}`),
            {
              tags: {
                stage,
                path: 'otp_modal',
                error_class: errName,
              },
              contexts: {
                auth_handoff: {
                  email_domain: session.user.email?.split('@')[1] ?? null,
                  is_facebook_browser: isFacebookBrowser,
                },
              },
            }
          );

          trackAuthEvent(posthog, 'signup_api_failed', {
            error: errMsg,
            error_class: errName,
            is_chunk_load: isChunkLoad,
            stage,
          }, session.user);

          grantOutcome(isChunkLoad ? 'failed_chunk_load' : 'failed_fetch', {
            supabase_user_id: session.user.id,
            error_class: errName,
            error_message: errMsg,
          });
          // Continue even if signup fails (user may already exist)
        }

        // Track signup or login event
        trackAuthEvent(posthog, isNewUser ? 'signup' : 'login', {
          email: session.user.email,
          method: 'email',
          external_id: session.user.id,
          user_id: session.user.id,
          is_new_user: isNewUser,
        }, session.user);

        // Track OTP completion (mirrors AuthCallbackPage's oauth_callback_completed)
        trackAuthEvent(posthog, 'otp_callback_completed', {
          auth_method: 'email_otp',
          is_new_user: isNewUser,
        }, session.user);
      } else {
        grantOutcome('skipped_no_session');
      }

      // Success - auth state will update via onAuthStateChange
      onVerified();
    } catch (err) {
      if (verifyOk) {
        grantOutcome('failed_other', {
          error_class: err instanceof Error ? err.name : 'Unknown',
          error_message: err instanceof Error ? err.message : String(err),
        });
      }
      setError(err instanceof Error ? err.message : 'Verification failed. Please try again.');
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || isResending) {
      return;
    }

    setIsResending(true);
    setError(null);

    posthog.capture('otp_resend_clicked', {
      time_since_original_ms: Date.now() - otpSentAtRef.current,
      is_facebook_browser: isFacebookBrowser,
    });

    try {
      const supabase = getSupabaseClient();
      const redirectTo = `${window.location.origin}/auth/callback`;

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          emailRedirectTo: redirectTo,
        },
      });

      if (error) {
        setError(error.message || 'Failed to resend code. Please try again.');
      } else {
        // Reset countdown and OTP sent timestamp
        otpSentAtRef.current = Date.now();
        setResendCooldown(30);
        // Clear code inputs
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
    } finally {
      setIsResending(false);
    }
  };

  const isCodeComplete = code.every((digit) => digit !== '') && code.join('').length === 6;

  return (
    <Portal>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 overflow-y-auto">
      <div className="bg-bg-panel border border-border rounded-modal p-4 sm:p-6 md:p-8 w-full max-w-[min(450px,100%)] my-auto">
        <h2 className="text-h1 font-medium text-white mb-1 sm:mb-2">Check your email</h2>
        <p className="text-body text-text-muted mb-4 sm:mb-6">to continue to GenAnything</p>

        {/* Email display with edit button */}
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center justify-center gap-2">
            <span className="text-white text-xs sm:text-sm break-all">{email}</span>
            <button
              onClick={() => {
                posthog.capture('otp_edit_email_clicked', {
                  is_facebook_browser: isFacebookBrowser,
                });
                onEditEmail();
              }}
              className="text-white/60 hover:text-white transition-colors flex-shrink-0"
              type="button"
            >
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* OTP Input Boxes */}
        <div className="flex gap-1.5 sm:gap-2 justify-center mb-4 sm:mb-6 px-2">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              className="w-10 h-10 sm:w-12 sm:h-12 text-center text-lg sm:text-xl font-medium bg-white text-black rounded-button border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white"
              disabled={loading}
            />
          ))}
        </div>

        {/* Resend Link */}
        <div className="text-center mb-4 sm:mb-6">
          <button
            onClick={handleResend}
            disabled={resendCooldown > 0 || isResending || loading}
            className="text-text-subtle hover:text-white text-body-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Didn&apos;t receive a code? Resend {resendCooldown > 0 ? `(${resendCooldown})` : ''}
          </button>
        </div>

        {/* Continue Button */}
        <button
          onClick={handleVerify}
          disabled={!isCodeComplete || loading}
          className="w-full bg-accent text-white btn-glow px-4 sm:px-6 py-3 min-h-[44px] rounded-button text-body font-medium hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-3 sm:mb-4"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              <span>Verifying...</span>
            </>
          ) : (
            <>
              <span>Continue</span>
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
            </>
          )}
        </button>

        {/* Error Message */}
        {error && (
          <div className="text-red-400 text-body-sm text-center mb-3 sm:mb-4">
            {error}
          </div>
        )}

        {/* Cancel Button */}
        <button
          onClick={() => {
            const codeEntered = code.some(digit => digit !== '');
            posthog.capture('otp_modal_dismissed', {
              time_on_modal_ms: Date.now() - modalOpenedAtRef.current,
              code_entered: codeEntered,
              is_facebook_browser: isFacebookBrowser,
            });
            onClose();
          }}
          className="w-full text-text-subtle hover:text-white transition-colors text-body-sm"
        >
          Cancel
        </button>
      </div>
      </div>
    </Portal>
  );
}

